import React, { useState, useEffect } from 'react';
import BlogManager from '../components/BlogManager';
import SourceAnalytics from '../components/SourceAnalytics';

const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  const [members, setMembers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSubscribed, setFilterSubscribed] = useState('all');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);

  // Check for existing token on mount
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      setIsAuthenticated(true);
      fetchDashboard();
    }
  }, []);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError('');

    try {
      const response = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      localStorage.setItem('adminToken', data.token);
      setIsAuthenticated(true);
      fetchDashboard();
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    setDashboardData(null);
    setMembers([]);
  };

  const fetchDashboard = async () => {
    try {
      const response = await fetch('/api/admin/dashboard', {
        headers: getAuthHeaders()
      });
      
      if (response.status === 401) {
        handleLogout();
        return;
      }

      const data = await response.json();
      setDashboardData(data);
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
    }
  };

  const fetchMembers = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        limit: pagination.limit,
        search: searchQuery,
        ...(filterSubscribed !== 'all' && { subscribed: filterSubscribed })
      });

      const response = await fetch(`/api/admin/members?${params}`, {
        headers: getAuthHeaders()
      });

      if (response.status === 401) {
        handleLogout();
        return;
      }

      const data = await response.json();
      setMembers(data.members);
      setPagination(data.pagination);
    } catch (err) {
      console.error('Failed to fetch members:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await fetch('/api/admin/analytics', {
        headers: getAuthHeaders()
      });

      if (response.status === 401) {
        handleLogout();
        return;
      }

      const data = await response.json();
      setAnalyticsData(data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to delete this member?')) return;

    try {
      const response = await fetch(`/api/admin/members/${memberId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        fetchMembers(pagination.page);
        fetchDashboard();
      }
    } catch (err) {
      console.error('Failed to delete member:', err);
    }
  };

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams({
        search: searchQuery,
        ...(filterSubscribed !== 'all' && { subscribed: filterSubscribed })
      });

      const response = await fetch(`/api/admin/members/export?${params}`, {
        headers: getAuthHeaders()
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `members-${Date.now()}.csv`;
      a.click();
    } catch (err) {
      console.error('Failed to export CSV:', err);
    }
  };

  useEffect(() => {
    if (isAuthenticated && activeTab === 'members') {
      fetchMembers(1);
    } else if (isAuthenticated && activeTab === 'analytics') {
      fetchAnalytics();
    }
  }, [isAuthenticated, activeTab, searchQuery, filterSubscribed]);

  const styles = {
    container: {
      minHeight: '100vh',
      background: '#f5f7fa'
    },
    loginContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '20px'
    },
    loginBox: {
      background: 'white',
      padding: '40px',
      borderRadius: '16px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
      width: '100%',
      maxWidth: '400px'
    },
    loginTitle: {
      fontSize: '1.8rem',
      color: '#1a1a2e',
      marginBottom: '10px',
      textAlign: 'center'
    },
    loginSubtitle: {
      color: '#666',
      textAlign: 'center',
      marginBottom: '30px'
    },
    input: {
      width: '100%',
      padding: '14px 16px',
      borderRadius: '8px',
      border: '2px solid #e0e0e0',
      fontSize: '1rem',
      marginBottom: '15px',
      boxSizing: 'border-box'
    },
    button: {
      width: '100%',
      padding: '14px',
      background: '#2055df',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer'
    },
    error: {
      color: '#d32f2f',
      background: '#ffebee',
      padding: '12px',
      borderRadius: '8px',
      marginBottom: '15px',
      textAlign: 'center'
    },
    layout: {
      display: 'flex',
      minHeight: '100vh'
    },
    sidebar: {
      width: '250px',
      background: '#1a1a2e',
      color: 'white',
      padding: '20px 0',
      position: 'fixed',
      height: '100vh',
      overflowY: 'auto'
    },
    sidebarHeader: {
      padding: '0 20px 20px',
      borderBottom: '1px solid rgba(255,255,255,0.1)'
    },
    sidebarLogo: {
      fontSize: '1.3rem',
      fontWeight: 'bold',
      color: '#fff'
    },
    sidebarNav: {
      padding: '20px 0'
    },
    navItem: {
      padding: '12px 20px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      transition: 'background 0.3s',
      borderLeft: '3px solid transparent'
    },
    navItemActive: {
      background: 'rgba(32, 85, 223, 0.3)',
      borderLeftColor: '#2055df'
    },
    main: {
      flex: 1,
      marginLeft: '250px',
      padding: '30px'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '30px'
    },
    pageTitle: {
      fontSize: '1.8rem',
      color: '#1a1a2e'
    },
    logoutBtn: {
      padding: '10px 20px',
      background: '#f44336',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '20px',
      marginBottom: '30px'
    },
    statCard: {
      background: 'white',
      padding: '24px',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
    },
    statLabel: {
      color: '#666',
      fontSize: '0.9rem',
      marginBottom: '8px'
    },
    statValue: {
      fontSize: '2rem',
      fontWeight: '700',
      color: '#1a1a2e'
    },
    card: {
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      overflow: 'hidden'
    },
    cardHeader: {
      padding: '20px',
      borderBottom: '1px solid #eee',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '15px'
    },
    cardTitle: {
      fontSize: '1.2rem',
      fontWeight: '600',
      color: '#1a1a2e'
    },
    filters: {
      display: 'flex',
      gap: '10px',
      flexWrap: 'wrap'
    },
    searchInput: {
      padding: '10px 16px',
      borderRadius: '8px',
      border: '1px solid #ddd',
      fontSize: '0.9rem',
      minWidth: '200px'
    },
    select: {
      padding: '10px 16px',
      borderRadius: '8px',
      border: '1px solid #ddd',
      fontSize: '0.9rem',
      background: 'white'
    },
    exportBtn: {
      padding: '10px 20px',
      background: '#4caf50',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '0.9rem'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    th: {
      textAlign: 'left',
      padding: '14px 16px',
      background: '#f8f9fa',
      color: '#666',
      fontSize: '0.85rem',
      fontWeight: '600',
      textTransform: 'uppercase'
    },
    td: {
      padding: '14px 16px',
      borderBottom: '1px solid #eee',
      fontSize: '0.95rem'
    },
    badge: {
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: '12px',
      fontSize: '0.8rem',
      fontWeight: '600'
    },
    badgeGreen: {
      background: '#e8f5e9',
      color: '#2e7d32'
    },
    badgeRed: {
      background: '#ffebee',
      color: '#c62828'
    },
    actionBtn: {
      padding: '6px 12px',
      borderRadius: '6px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '0.85rem',
      marginRight: '8px'
    },
    viewBtn: {
      background: '#e3f2fd',
      color: '#1976d2'
    },
    deleteBtn: {
      background: '#ffebee',
      color: '#c62828'
    },
    pagination: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '20px',
      borderTop: '1px solid #eee'
    },
    pageBtn: {
      padding: '8px 16px',
      border: '1px solid #ddd',
      borderRadius: '6px',
      background: 'white',
      cursor: 'pointer',
      marginRight: '8px'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    },
    modalContent: {
      background: 'white',
      padding: '30px',
      borderRadius: '16px',
      maxWidth: '500px',
      width: '90%',
      maxHeight: '80vh',
      overflow: 'auto'
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px'
    },
    closeBtn: {
      background: 'none',
      border: 'none',
      fontSize: '1.5rem',
      cursor: 'pointer',
      color: '#666'
    },
    detailRow: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '12px 0',
      borderBottom: '1px solid #eee'
    },
    detailLabel: {
      color: '#666',
      fontWeight: '500'
    },
    detailValue: {
      color: '#1a1a2e',
      fontWeight: '600'
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div style={styles.loginContainer}>
        <div style={styles.loginBox}>
          <h1 style={styles.loginTitle}>🔐 Admin Login</h1>
          <p style={styles.loginSubtitle}>Rx.ChatWithDr Dashboard</p>
          
          {loginError && <div style={styles.error}>{loginError}</div>}
          
          <form onSubmit={handleLogin}>
            <input
              type="email"
              placeholder="Email"
              value={loginData.email}
              onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
              style={styles.input}
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={loginData.password}
              onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
              style={styles.input}
              required
            />
            <button type="submit" style={styles.button} disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Dashboard Content
  return (
    <div style={styles.container}>
      <div style={styles.layout}>
        {/* Sidebar */}
        <div style={styles.sidebar}>
          <div style={styles.sidebarHeader}>
            <div style={styles.sidebarLogo}>💊 Rx.ChatWithDr</div>
            <div style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: '5px' }}>Admin Panel</div>
          </div>
          <nav style={styles.sidebarNav}>
            {[
              { id: 'dashboard', icon: '📊', label: 'Dashboard' },
              { id: 'blog', icon: '📝', label: 'Blog Manager' },
              { id: 'sources', icon: '📡', label: 'Source Analytics' },
              { id: 'members', icon: '👥', label: 'Members' },
              { id: 'analytics', icon: '📈', label: 'Analytics' },
              { id: 'export', icon: '📥', label: 'Export Data' },
              { id: 'settings', icon: '⚙️', label: 'Settings' }
            ].map(item => (
              <div
                key={item.id}
                style={{
                  ...styles.navItem,
                  ...(activeTab === item.id ? styles.navItemActive : {})
                }}
                onClick={() => setActiveTab(item.id)}
              >
                <span>{item.icon}</span>
                <span>{item.label}</span>
              </div>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        <main style={styles.main}>
          <div style={styles.header}>
            <h1 style={styles.pageTitle}>
              {activeTab === 'dashboard' && '📊 Dashboard'}
              {activeTab === 'blog' && '📝 Blog Manager'}
              {activeTab === 'sources' && '📡 Source Analytics'}
              {activeTab === 'members' && '👥 Members'}
              {activeTab === 'analytics' && '📈 Analytics'}
              {activeTab === 'export' && '📥 Export Data'}
              {activeTab === 'settings' && '⚙️ Settings'}
            </h1>
            <button style={styles.logoutBtn} onClick={handleLogout}>
              Logout
            </button>
          </div>

          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && dashboardData && (
            <>
              <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                  <div style={styles.statLabel}>Total Members</div>
                  <div style={styles.statValue}>{dashboardData.stats.totalMembers}</div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statLabel}>Subscribed</div>
                  <div style={{ ...styles.statValue, color: '#2e7d32' }}>
                    {dashboardData.stats.subscribedMembers}
                  </div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statLabel}>Subscription Rate</div>
                  <div style={{ ...styles.statValue, color: '#2055df' }}>
                    {dashboardData.stats.subscriptionRate}%
                  </div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statLabel}>New This Week</div>
                  <div style={{ ...styles.statValue, color: '#ff9800' }}>
                    {dashboardData.stats.newThisWeek}
                  </div>
                </div>
              </div>

              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <h2 style={styles.cardTitle}>Recent Members</h2>
                </div>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Member ID</th>
                      <th style={styles.th}>Name</th>
                      <th style={styles.th}>Email</th>
                      <th style={styles.th}>Subscribed</th>
                      <th style={styles.th}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.recentMembers.map(member => (
                      <tr key={member.memberId}>
                        <td style={styles.td}>{member.memberId}</td>
                        <td style={styles.td}>{member.firstName} {member.lastName}</td>
                        <td style={styles.td}>{member.email}</td>
                        <td style={styles.td}>
                          <span style={{
                            ...styles.badge,
                            ...(member.subscribePromotions ? styles.badgeGreen : styles.badgeRed)
                          }}>
                            {member.subscribePromotions ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td style={styles.td}>
                          {new Date(member.createdAt).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Blog Manager Tab */}
          {activeTab === 'blog' && (
            <BlogManager getAuthHeaders={getAuthHeaders} handleLogout={handleLogout} />
          )}

          {/* Source Analytics Tab */}
          {activeTab === 'sources' && (
            <SourceAnalytics getAuthHeaders={getAuthHeaders} handleLogout={handleLogout} />
          )}

          {/* Members Tab */}
          {activeTab === 'members' && (
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h2 style={styles.cardTitle}>All Members</h2>
                <div style={styles.filters}>
                  <input
                    type="text"
                    placeholder="Search members..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={styles.searchInput}
                  />
                  <select
                    value={filterSubscribed}
                    onChange={(e) => setFilterSubscribed(e.target.value)}
                    style={styles.select}
                  >
                    <option value="all">All Members</option>
                    <option value="true">Subscribed</option>
                    <option value="false">Not Subscribed</option>
                  </select>
                  <button style={styles.exportBtn} onClick={handleExportCSV}>
                    📥 Export CSV
                  </button>
                </div>
              </div>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Member ID</th>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Email</th>
                    <th style={styles.th}>Phone</th>
                    <th style={styles.th}>Subscribed</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map(member => (
                    <tr key={member.memberId}>
                      <td style={styles.td}>{member.memberId}</td>
                      <td style={styles.td}>{member.firstName} {member.lastName}</td>
                      <td style={styles.td}>{member.email}</td>
                      <td style={styles.td}>{member.phone}</td>
                      <td style={styles.td}>
                        <span style={{
                          ...styles.badge,
                          ...(member.subscribePromotions ? styles.badgeGreen : styles.badgeRed)
                        }}>
                          {member.subscribePromotions ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <button
                          style={{ ...styles.actionBtn, ...styles.viewBtn }}
                          onClick={() => setSelectedMember(member)}
                        >
                          View
                        </button>
                        <button
                          style={{ ...styles.actionBtn, ...styles.deleteBtn }}
                          onClick={() => handleDeleteMember(member.memberId)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={styles.pagination}>
                <div>
                  Showing {members.length} of {pagination.total} members
                </div>
                <div>
                  <button
                    style={styles.pageBtn}
                    disabled={pagination.page === 1}
                    onClick={() => fetchMembers(pagination.page - 1)}
                  >
                    Previous
                  </button>
                  <span style={{ margin: '0 15px' }}>
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <button
                    style={styles.pageBtn}
                    disabled={pagination.page === pagination.pages}
                    onClick={() => fetchMembers(pagination.page + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && analyticsData && (
            <>
              <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                  <div style={styles.statLabel}>Total Members</div>
                  <div style={styles.statValue}>{analyticsData.summary.totalMembers}</div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statLabel}>Subscription Rate</div>
                  <div style={{ ...styles.statValue, color: '#2055df' }}>
                    {analyticsData.summary.subscriptionRate}%
                  </div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statLabel}>New This Week</div>
                  <div style={{ ...styles.statValue, color: '#ff9800' }}>
                    {analyticsData.summary.newMembersThisWeek}
                  </div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statLabel}>New This Month</div>
                  <div style={{ ...styles.statValue, color: '#4caf50' }}>
                    {analyticsData.summary.newMembersThisMonth}
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div style={styles.card}>
                  <div style={styles.cardHeader}>
                    <h2 style={styles.cardTitle}>Top ZIP Codes</h2>
                  </div>
                  <div style={{ padding: '20px' }}>
                    {analyticsData.topZipCodes.map((item, idx) => (
                      <div key={item.zipCode} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '10px 0',
                        borderBottom: '1px solid #eee'
                      }}>
                        <span>{idx + 1}. {item.zipCode}</span>
                        <span style={{ fontWeight: '600' }}>{item.count} members</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={styles.card}>
                  <div style={styles.cardHeader}>
                    <h2 style={styles.cardTitle}>Registration Trend (30 days)</h2>
                  </div>
                  <div style={{ padding: '20px' }}>
                    {analyticsData.registrationTrend.slice(-10).map(item => (
                      <div key={item.date} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '8px 0',
                        borderBottom: '1px solid #eee'
                      }}>
                        <span>{item.date}</span>
                        <span style={{ fontWeight: '600' }}>{item.count} new</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Export Tab */}
          {activeTab === 'export' && (
            <div style={styles.card}>
              <div style={{ padding: '30px' }}>
                <h2 style={{ marginBottom: '20px' }}>Export Data</h2>
                <p style={{ color: '#666', marginBottom: '30px' }}>
                  Download member data in CSV format for analysis or backup.
                </p>
                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                  <button
                    style={{ ...styles.button, width: 'auto', padding: '14px 30px' }}
                    onClick={handleExportCSV}
                  >
                    📥 Export All Members
                  </button>
                  <button
                    style={{ ...styles.button, width: 'auto', padding: '14px 30px', background: '#4caf50' }}
                    onClick={() => {
                      setFilterSubscribed('true');
                      handleExportCSV();
                    }}
                  >
                    📧 Export Subscribed Only
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div style={styles.card}>
              <div style={{ padding: '30px' }}>
                <h2 style={{ marginBottom: '20px' }}>Settings</h2>
                <div style={{ ...styles.detailRow, borderBottom: '1px solid #eee' }}>
                  <span style={styles.detailLabel}>Admin Email</span>
                  <span style={styles.detailValue}>{localStorage.getItem('adminEmail') || 'admin@chatwithdr.com'}</span>
                </div>
                <div style={{ ...styles.detailRow, borderBottom: '1px solid #eee' }}>
                  <span style={styles.detailLabel}>API Status</span>
                  <span style={{ ...styles.badge, ...styles.badgeGreen }}>Connected</span>
                </div>
                <div style={{ ...styles.detailRow, borderBottom: '1px solid #eee' }}>
                  <span style={styles.detailLabel}>Database</span>
                  <span style={{ ...styles.badge, ...styles.badgeGreen }}>MongoDB Atlas</span>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Member Detail Modal */}
      {selectedMember && (
        <div style={styles.modal} onClick={() => setSelectedMember(null)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2>Member Details</h2>
              <button style={styles.closeBtn} onClick={() => setSelectedMember(null)}>×</button>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Member ID</span>
              <span style={styles.detailValue}>{selectedMember.memberId}</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Name</span>
              <span style={styles.detailValue}>{selectedMember.firstName} {selectedMember.lastName}</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Email</span>
              <span style={styles.detailValue}>{selectedMember.email}</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Phone</span>
              <span style={styles.detailValue}>{selectedMember.phone}</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>ZIP Code</span>
              <span style={styles.detailValue}>{selectedMember.zipCode || 'N/A'}</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Subscribed</span>
              <span style={{
                ...styles.badge,
                ...(selectedMember.subscribePromotions ? styles.badgeGreen : styles.badgeRed)
              }}>
                {selectedMember.subscribePromotions ? 'Yes' : 'No'}
              </span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Registered</span>
              <span style={styles.detailValue}>
                {new Date(selectedMember.createdAt).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;